import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import SearchableSelect from 'shared/components/SearchableSelect';
import Toast from 'shared/components/Toast';
import UserChangeConfirmationModal, {
  ChangeItem,
} from '../components/admin/UserChangeConfirmationModal';
import adminService from '../services/adminService';
import { AdminUpdateUserDto } from '../types/admin';
import { User, UserRole } from '../types/auth';
import styles from './AdminUserDetail.module.css';

import {
  affiliationsData,
  facultadesData,
  unidadesData,
} from '../utils/affiliations';

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'error' | 'success' | 'warning' | 'info';
  } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'researcher' as UserRole,
    isActive: true,
    run: '',
    affiliation: '',
    facultadUC: '',
    unidadAcademica: '',
    researcherType: '' as 'consolidated' | 'emerging' | '',
    doctorateYear: '',
    canApply: false,
    platformMessage: '',
  });

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [changes, setChanges] = useState<ChangeItem[]>([]);

  useEffect(() => {
    if (id) {
      loadUser(id);
    }
  }, [id]);

  const loadUser = async (userId: string) => {
    try {
      setLoading(true);
      setError(null);

      const userData = await adminService.getUserById(userId);
      setUser(userData);

      // Populate form with user data
      setFormData({
        name: userData.name || '',
        email: userData.email || '',
        role: userData.role || 'researcher',
        isActive: userData.isActive ?? true,
        run: userData.userProfile?.run || '',
        affiliation: userData.userProfile?.affiliation || '',
        facultadUC: userData.userProfile?.facultadUC || '',
        unidadAcademica: userData.userProfile?.unidadAcademica || '',
        researcherType: userData.userProfile?.researcherType || '',
        doctorateYear: userData.userProfile?.doctorateYear?.toString() || '',
        canApply: userData.userProfile?.canApply ?? false,
        platformMessage: userData.userProfile?.platformMessage || '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const getRoleLabel = (role: UserRole): string => {
    const roleMap: Record<UserRole, string> = {
      researcher: 'Investigador',
      admin: 'Administrador',
      evaluator: 'Evaluador',
    };
    return roleMap[role] || role;
  };

  const getResearcherTypeLabel = (type: string): string => {
    if (!type) return '-';
    return type === 'consolidated' ? 'Consolidado' : 'Emergente';
  };

  const detectChanges = (): ChangeItem[] => {
    if (!user) return [];

    const detectedChanges: ChangeItem[] = [];

    // Check user fields
    if (formData.name !== user.name) {
      detectedChanges.push({
        field: 'name',
        label: 'Nombre',
        oldValue: user.name,
        newValue: formData.name,
      });
    }

    if (formData.email !== user.email) {
      detectedChanges.push({
        field: 'email',
        label: 'Email',
        oldValue: user.email,
        newValue: formData.email,
      });
    }

    if (formData.role !== user.role) {
      detectedChanges.push({
        field: 'role',
        label: 'Rol',
        oldValue: getRoleLabel(user.role),
        newValue: getRoleLabel(formData.role),
      });
    }

    if (formData.isActive !== user.isActive) {
      detectedChanges.push({
        field: 'isActive',
        label: 'Activo',
        oldValue: user.isActive,
        newValue: formData.isActive,
      });
    }

    // Check profile fields
    const profile = user.userProfile || {};

    if (formData.run !== (profile.run || '')) {
      detectedChanges.push({
        field: 'run',
        label: 'RUN',
        oldValue: profile.run || '-',
        newValue: formData.run || '-',
      });
    }

    if (formData.affiliation !== (profile.affiliation || '')) {
      detectedChanges.push({
        field: 'affiliation',
        label: 'Acceso a',
        oldValue: profile.affiliation || '-',
        newValue: formData.affiliation || '-',
      });
    }

    if (formData.facultadUC !== (profile.facultadUC || '')) {
      detectedChanges.push({
        field: 'facultadUC',
        label: 'Facultad UC',
        oldValue: profile.facultadUC || '-',
        newValue: formData.facultadUC || '-',
      });
    }

    if (formData.unidadAcademica !== (profile.unidadAcademica || '')) {
      detectedChanges.push({
        field: 'unidadAcademica',
        label: 'Unidad Académica',
        oldValue: profile.unidadAcademica || '-',
        newValue: formData.unidadAcademica || '-',
      });
    }

    if (formData.researcherType !== (profile.researcherType || '')) {
      detectedChanges.push({
        field: 'researcherType',
        label: 'Tipo de Investigador',
        oldValue: getResearcherTypeLabel(profile.researcherType || ''),
        newValue: getResearcherTypeLabel(formData.researcherType),
      });
    }

    const oldDoctorateYear = profile.doctorateYear?.toString() || '';
    if (formData.doctorateYear !== oldDoctorateYear) {
      detectedChanges.push({
        field: 'doctorateYear',
        label: 'Año de Doctorado',
        oldValue: oldDoctorateYear || '-',
        newValue: formData.doctorateYear || '-',
      });
    }

    if (formData.canApply !== (profile.canApply ?? false)) {
      detectedChanges.push({
        field: 'canApply',
        label: 'Puede Postular',
        oldValue: profile.canApply ?? false,
        newValue: formData.canApply,
      });
    }

    if (formData.platformMessage !== (profile.platformMessage || '')) {
      detectedChanges.push({
        field: 'platformMessage',
        label: 'Mensaje de error de la plataforma',
        oldValue: profile.platformMessage || '-',
        newValue: formData.platformMessage || '-',
      });
    }

    return detectedChanges;
  };

  const handleSaveClick = () => {
    const detectedChanges = detectChanges();
    setChanges(detectedChanges);
    setShowConfirmModal(true);
  };

  const hasChanges = () => {
    return detectChanges().length > 0;
  };

  const handleConfirmSave = async () => {
    if (!id) return;

    try {
      setSaving(true);

      const updateDto: AdminUpdateUserDto = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
        profile: {
          run: formData.run || undefined,
          affiliation: formData.affiliation || undefined,
          facultadUC: formData.facultadUC || undefined,
          unidadAcademica: formData.unidadAcademica || undefined,
          researcherType: formData.researcherType || undefined,
          doctorateYear: formData.doctorateYear
            ? parseInt(formData.doctorateYear)
            : undefined,
          canApply: formData.canApply,
          platformMessage: formData.platformMessage || undefined,
        },
      };

      await adminService.updateUser(id, updateDto);

      setToast({
        message: 'Usuario actualizado exitosamente',
        type: 'success',
      });

      setShowConfirmModal(false);

      // Reload user data
      await loadUser(id);
    } catch (err) {
      setToast({
        message:
          err instanceof Error ? err.message : 'Error al actualizar usuario',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  if (loading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          <div className={styles.loadingMessage}>Cargando usuario...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          <div className={styles.error}>
            <h2>Error</h2>
            <p>{error}</p>
            <Button onClick={handleCancel} variant="primary">
              Volver
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          <div className={styles.error}>
            <h2>Usuario no encontrado</h2>
            <Button onClick={handleCancel} variant="primary">
              Volver
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.headerContainer}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Editar Usuario</h1>
            <p className={styles.subtitle}>
              Actualiza la información del usuario
            </p>
          </div>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.content}>
          <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
            {/* Basic Information Section */}
            <section className={styles.formSection}>
              <h2 className={styles.sectionTitle}>Información Básica</h2>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="name" className={styles.label}>
                    Nombre <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email" className={styles.label}>
                    Email <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="run" className={styles.label}>
                    RUN
                  </label>
                  <input
                    type="text"
                    id="run"
                    name="run"
                    value={formData.run}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="12.345.678-9"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="role" className={styles.label}>
                    Rol <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className={styles.select}
                    required
                  >
                    <option value="researcher">Investigador</option>
                    <option value="admin">Administrador</option>
                    <option value="evaluator">Evaluador</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      name="canApply"
                      checked={formData.canApply}
                      onChange={handleInputChange}
                      className={styles.checkbox}
                    />
                    <span>Puede postular</span>
                  </label>
                </div>
              </div>
            </section>

            {/* Academic Information Section */}
            <section className={styles.formSection}>
              <h2 className={styles.sectionTitle}>Información Académica</h2>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <SearchableSelect
                    id="affiliation"
                    name="affiliation"
                    label="Acceso a"
                    options={affiliationsData}
                    value={formData.affiliation}
                    onChange={handleInputChange}
                    placeholder="Buscar o ingresar afiliación..."
                    allowManualEntry={true}
                  />
                </div>

                <div className={styles.formGroup}>
                  <SearchableSelect
                    id="facultadUC"
                    name="facultadUC"
                    label="Facultad UC"
                    options={facultadesData}
                    value={formData.facultadUC}
                    onChange={handleInputChange}
                    placeholder="Buscar o ingresar facultad..."
                    allowManualEntry={true}
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <SearchableSelect
                    id="unidadAcademica"
                    name="unidadAcademica"
                    label="Unidad Académica"
                    options={unidadesData}
                    value={formData.unidadAcademica}
                    onChange={handleInputChange}
                    placeholder="Buscar o ingresar unidad académica..."
                    allowManualEntry={true}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="researcherType" className={styles.label}>
                    Tipo de Investigador
                  </label>
                  <select
                    id="researcherType"
                    name="researcherType"
                    value={formData.researcherType}
                    onChange={handleInputChange}
                    className={styles.select}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="consolidated">Consolidado</option>
                    <option value="emerging">Emergente</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="doctorateYear" className={styles.label}>
                    Año de Doctorado
                  </label>
                  <input
                    type="number"
                    id="doctorateYear"
                    name="doctorateYear"
                    value={formData.doctorateYear}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="2020"
                    min="1950"
                    max={new Date().getFullYear()}
                  />
                </div>
              </div>
            </section>

            {/* Platform Settings Section */}
            <section className={styles.formSection}>
              <h2 className={styles.sectionTitle}>
                Configuración de Plataforma
              </h2>

              <div className={styles.formGroup}>
                <label htmlFor="platformMessage" className={styles.label}>
                  Mensaje de error de la plataforma
                </label>
                <textarea
                  id="platformMessage"
                  name="platformMessage"
                  value={formData.platformMessage}
                  onChange={handleInputChange}
                  className={styles.textarea}
                  rows={4}
                  placeholder="Mensaje personalizado para mostrar al usuario en la plataforma"
                />
              </div>
            </section>

            {/* Form Actions */}
            <div className={styles.formActions}>
              <Button onClick={handleCancel} variant="secondary">
                Cancelar
              </Button>
              <Button
                onClick={handleSaveClick}
                variant="primary"
                disabled={!hasChanges()}
              >
                Guardar
              </Button>
            </div>
          </form>
        </div>
      </div>

      <UserChangeConfirmationModal
        isOpen={showConfirmModal}
        changes={changes}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowConfirmModal(false)}
        isLoading={saving}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AdminUserDetail;
